/**
 * Cause attribution for the Reach Wire and the price chips (spec §12.2). For an effect at station S and
 * ware W (a shortage or a rising price on the `short` side, a surplus or a falling price on the `glut`
 * side), the candidates of the last `wire.json causeDays` faction days are scored and the best one wins:
 *
 * | Cause | Evidence (notable ring, spec §11.4 field conventions) | Score (`causeScores`) |
 * |---|---|---|
 * | `lostCarrier` | `agentDestroyed` / `convoyLost` whose destination is S and cargo is W | 1.0 |
 * | `upstreamStarved` | a line producing W, within `UPSTREAM_JUMPS` of S, starved of an input (`stationStarved`, or still flagged) | 0.8 |
 * | `raidNearby` | a `raid`, a lost convoy or a non-raider loss in S's system or within `NEARBY_JUMPS` | 0.6 |
 * | `demandJump` | a shipyard order whose hull costs W, or a construction start whose template costs W, within `NEARBY_JUMPS` | 0.5 |
 * | `playerDump` (glut side) | a player sale of W to S of at least `dumpShare` × capacity | 0.5 |
 * | `strongDemand` / `oversupply` | the fallback of each side | 0 |
 *
 * Ties break deterministically: the higher score, then the lower `CAUSE_KIND` code, then the newest
 * event (the ring is scanned newest first and a candidate replaces the best only when strictly better),
 * then the lowest station index for live line state. Habitat growth is not a demand-jump source in M4:
 * no event records it (M5 may add one).
 *
 * Runs at most a few times per faction day (Wire items) and per view build (price chips), never per tick.
 * The context is a structural slice of `StrategicState`, so the sim passes its state and tests pass a
 * hand-filled `allocateState`.
 */
import { systemId as formatSystemId } from '../../galaxy/placement'
import { eventSlot, firstReadable } from '../events'
import { sw } from '../economy/stock'
import {
  CAUSE_KIND,
  EV,
  FACTION,
  FACTION_IDS,
  LINE_STATUS,
  SIDE,
  UNREACHABLE,
  type CauseKindName,
  type RoutingTables,
  type StrategicState,
} from '../types'
import { WARE_COUNT, WARE_IDS } from '../wares'
import { causePhrases, pickVariant, render, type WireFields } from './templates'

/** What cause attribution, the Wire and the price chips read: a structural slice of `StrategicState`. */
export type WireContext = Pick<
  StrategicState,
  'seed' | 'time' | 'day' | 'data' | 'galaxy' | 'stations' | 'lines' | 'agents' | 'routing' | 'notable'
>

/**
 * Spec §12.2 ranges: an upstream producer counts within 2 jumps; a raid, a loss or a demand source counts
 * in S's system or a neighbour. D-TBD (gate): move both into wire.json (`upstreamJumps`, `nearbyJumps`)
 * with the E0 schema; they are spec constants here until then.
 */
export const UPSTREAM_JUMPS = 2
export const NEARBY_JUMPS = 1

/** `short`: a shortage or a rising price. `glut`: a surplus or a falling price. */
export type EffectSide = 'short' | 'glut'

export interface CauseEffect {
  side: EffectSide
  station: number
  ware: number
}

/** An attributed cause. `station`, `sys`, `ware` and `faction` are what the cause phrase names. */
export interface Cause {
  kind: CauseKindName
  score: number
  /** The station the phrase names (the lost run's source, the starved producer, the yard), −1 = none (the system name stands in). */
  station: number
  sys: number
  /** The ware the phrase names: W, or the missing input of a starved producer. */
  ware: number
  faction: number
  /** Sequence number of the deciding event in `notable`, −1 for a fallback or live line state. */
  event: number
}

export function createCause(): Cause {
  return { kind: 'strongDemand', score: 0, station: -1, sys: 0, ware: -1, faction: 0, event: -1 }
}

/** Relay jumps between two systems over online relays (0 for the same system, UNREACHABLE when none). */
export function jumpsBetween(routing: RoutingTables, a: number, b: number): number {
  if (a === b) return 0
  const n = routing.systems
  if (a < 0 || b < 0 || a >= n || b >= n) return UNREACHABLE
  return routing.hops[a * n + b]
}

function set(c: Cause, kind: CauseKindName, score: number, station: number, sys: number, ware: number, faction: number, event: number): void {
  c.kind = kind
  c.score = score
  c.station = station
  c.sys = sys
  c.ware = ware
  c.faction = faction
  c.event = event
}

function better(c: Cause, kind: CauseKindName, score: number): boolean {
  return score > c.score || (score === c.score && CAUSE_KIND[kind] < CAUSE_KIND[c.kind])
}

/** The station a lost agent was supplied from, when its pool slot still holds it (freed agents keep their columns until reused). */
function lostRunSource(ctx: WireContext, agent: number, seq: number): number {
  const p = ctx.agents
  if (agent < 0 || agent >= p.capacity || p.seq[agent] !== seq) return -1
  const src = p.src[agent]
  return src >= 0 && src < ctx.stations.count ? src : -1
}

function hullCosts(ctx: WireContext, hull: number, w: number): boolean {
  const k = hull * WARE_COUNT + w
  return hull >= 0 && k < ctx.data.hullCost.length && ctx.data.hullCost[k] > 0
}

function templateCosts(ctx: WireContext, template: number, w: number): boolean {
  const t = ctx.data.templates.templates[template]
  if (t === undefined || w < 0 || w >= WARE_COUNT) return false
  const id = WARE_IDS[w]
  for (const level of t.buildCost) if ((level[id] ?? 0) > 0) return true
  return false
}

function lineProduces(ctx: WireContext, station: number, line: number, w: number): boolean {
  if (station < 0 || station >= ctx.stations.count || line < 0 || line >= ctx.lines.perStation) return false
  const r = ctx.lines.recipe[station * ctx.lines.perStation + line]
  return r >= 0 && r < ctx.data.recipes.length && ctx.data.recipes[r].outputs[w] > 0
}

/**
 * The best cause of `effect` (spec §12.2), written into `out` (a fresh record when omitted). Always returns
 * a cause: the side's fallback (`strongDemand` / `oversupply`, score 0) when no evidence scores.
 */
export function attributeCause(ctx: WireContext, effect: CauseEffect, out: Cause = createCause()): Cause {
  const { data, stations: st, notable: ring } = ctx
  const s = effect.station
  const w = effect.ware
  const sysS = st.sys[s]
  const scores = data.wire.causeScores
  const short = effect.side === 'short'
  set(out, short ? 'strongDemand' : 'oversupply', 0, s, sysS, w, st.owner[s], -1)

  const dayS = data.strategic.clock.dayS
  const from = ctx.time - data.wire.causeDays * dayS
  // Coarse substeps stamp bulk events at the substep end, so the ring is time-ordered only to within a substep.
  const stopBelow = from - dayS / data.strategic.clock.coarseSubsteps
  const oldest = firstReadable(ring, 0)
  const dumpQty = data.wire.dumpShare * st.cap[sw(s, w)]

  for (let i = ring.head - 1; i >= oldest; i--) {
    const k = eventSlot(ring, i)
    const t = ring.t[k]
    if (t < stopBelow) break
    if (t < from || t > ctx.time) continue
    const type = ring.type[k]
    const esys = ring.sys[k]
    if (!short) {
      if (
        type === EV.tradeDone &&
        ring.agent[k] < 0 &&
        ring.station[k] === s &&
        ring.ware[k] === w &&
        ring.cause[k] === SIDE.sell &&
        ring.qty[k] >= dumpQty &&
        better(out, 'playerDump', scores.playerDump)
      ) {
        set(out, 'playerDump', scores.playerDump, s, sysS, w, ring.faction[k], i)
      }
      continue
    }
    switch (type) {
      case EV.agentDestroyed:
      case EV.convoyLost: {
        if (ring.station[k] === s && ring.ware[k] === w) {
          if (better(out, 'lostCarrier', scores.lostCarrier)) {
            const src = type === EV.agentDestroyed ? lostRunSource(ctx, ring.agent[k], ring.aux[k]) : -1
            set(out, 'lostCarrier', scores.lostCarrier, src, esys, w, ring.faction[k], i)
          }
        } else if (
          (type === EV.convoyLost || ring.faction[k] !== FACTION.raider) &&
          jumpsBetween(ctx.routing, sysS, esys) <= NEARBY_JUMPS &&
          better(out, 'raidNearby', scores.raidNearby)
        ) {
          set(out, 'raidNearby', scores.raidNearby, -1, esys, w, ring.other[k], i)
        }
        break
      }
      case EV.raid:
        if (jumpsBetween(ctx.routing, sysS, esys) <= NEARBY_JUMPS && better(out, 'raidNearby', scores.raidNearby)) {
          set(out, 'raidNearby', scores.raidNearby, -1, esys, w, ring.faction[k], i)
        }
        break
      case EV.stationStarved: {
        const p = ring.station[k]
        if (
          ring.cause[k] === LINE_STATUS.starved &&
          ring.ware[k] >= 0 &&
          lineProduces(ctx, p, ring.aux[k], w) &&
          jumpsBetween(ctx.routing, sysS, st.sys[p]) <= UPSTREAM_JUMPS &&
          better(out, 'upstreamStarved', scores.upstreamStarved)
        ) {
          set(out, 'upstreamStarved', scores.upstreamStarved, p, st.sys[p], ring.ware[k], st.owner[p], i)
        }
        break
      }
      case EV.orderPlaced: {
        const yard = ring.station[k]
        if (
          yard >= 0 &&
          yard < st.count &&
          hullCosts(ctx, ring.cause[k], w) &&
          jumpsBetween(ctx.routing, sysS, st.sys[yard]) <= NEARBY_JUMPS &&
          better(out, 'demandJump', scores.demandJump)
        ) {
          set(out, 'demandJump', scores.demandJump, yard, st.sys[yard], w, ring.faction[k], i)
        }
        break
      }
      case EV.constructionStarted:
        if (
          templateCosts(ctx, ring.cause[k], w) &&
          jumpsBetween(ctx.routing, sysS, esys) <= NEARBY_JUMPS &&
          better(out, 'demandJump', scores.demandJump)
        ) {
          set(out, 'demandJump', scores.demandJump, -1, esys, w, ring.faction[k], i)
        }
        break
      default:
        break
    }
  }

  // A producer still starved whose event has left the window (or the ring) still counts.
  if (short && better(out, 'upstreamStarved', scores.upstreamStarved)) {
    const L = ctx.lines
    for (let p = 0; p < st.count; p++) {
      if (jumpsBetween(ctx.routing, sysS, st.sys[p]) > UPSTREAM_JUMPS) continue
      for (let l = 0; l < L.count[p]; l++) {
        const idx = p * L.perStation + l
        if (L.flagged[idx] === 1 && L.status[idx] === LINE_STATUS.starved && L.missing[idx] >= 0 && lineProduces(ctx, p, l, w)) {
          set(out, 'upstreamStarved', scores.upstreamStarved, p, st.sys[p], L.missing[idx], st.owner[p], -1)
          return out
        }
      }
    }
  }
  return out
}

// ---------------------------------------------------------------------------------------------
// Names and the cause phrase
// ---------------------------------------------------------------------------------------------

export function systemName(ctx: Pick<WireContext, 'galaxy'>, sys: number): string {
  return ctx.galaxy.systems[sys]?.name ?? formatSystemId(sys)
}

export function stationName(ctx: Pick<WireContext, 'galaxy' | 'stations'>, station: number, sysFallback: number): string {
  if (station >= 0 && station < ctx.stations.count) return ctx.stations.name[station] ?? ctx.stations.id[station]
  return systemName(ctx, sysFallback)
}

export function wareName(ctx: Pick<WireContext, 'data'>, w: number): string {
  return ctx.data.wares.wares[w]?.name ?? ''
}

export function factionName(ctx: Pick<WireContext, 'data'>, f: number): string {
  return ctx.data.factions.factions[f]?.name ?? ''
}

/** The faction id of a faction code, or null for FACTION_NONE / FACTION_PLAYER. */
export function factionIdOf(f: number): (typeof FACTION_IDS)[number] | null {
  return f >= 0 && f < FACTION_IDS.length ? FACTION_IDS[f] : null
}

/** The slot values a cause names (its station, system, ware and faction). */
export function causeFields(ctx: WireContext, cause: Cause): WireFields {
  return {
    station: stationName(ctx, cause.station, cause.sys),
    system: systemName(ctx, cause.sys),
    ware: cause.ware >= 0 ? wareName(ctx, cause.ware) : '',
    faction: factionName(ctx, cause.faction),
  }
}

/** The cause phrase (" after raiders hit a supply run from …") with the variant seeded by Wire item `id`. */
export function renderCause(ctx: WireContext, cause: Cause, id: number): string {
  return render(pickVariant(causePhrases(ctx.data, cause.kind), ctx.seed, 'wireCause', id), causeFields(ctx, cause))
}
